import { defaultConfig, getNodeDefinition } from './catalogue';
import type { FlowGraph } from './graph';

export interface FlowTemplate {
  key: string;
  name: string;
  description: string;
  /** The card's footnote, e.g. "9 nodes · most popular". */
  note: string;
  graph: FlowGraph;
}

type Step = [id: string, type: string, config?: Record<string, unknown>];
type Link = [from: string, to: string, fromPort?: string, toPort?: string];

const PER_ROW = 3;
const COLUMN = 380;
const ROW = 220;

/** Lays steps out three to a row (as the editor screens do), with the given links. */
function graphOf(steps: Step[], links: Link[]): FlowGraph {
  return {
    schemaVersion: 1,
    nodes: steps.map(([id, type, config = {}], i) => {
      const definition = getNodeDefinition(type);
      if (!definition) throw new Error(`Template uses unknown node type ${type}`);
      return {
        id,
        type,
        typeVersion: definition.version,
        position: { x: (i % PER_ROW) * COLUMN, y: Math.floor(i / PER_ROW) * ROW },
        config: { ...defaultConfig(definition), ...config },
      };
    }),
    edges: links.map(([from, to, fromPort = 'out', toPort = 'in'], i) => ({
      id: `e${i + 1}`,
      source: { nodeId: from, port: fromPort },
      target: { nodeId: to, port: toPort },
    })),
  };
}

const chain = (...ids: string[]): Link[] => ids.slice(1).map((id, i) => [ids[i]!, id]);

export const FLOW_TEMPLATES: readonly FlowTemplate[] = [
  {
    key: 'replicate-competitor-winner',
    name: 'Replicate a competitor winner',
    description:
      'Remake over-performing competitor reels in your brand voice — 5 hooks each.',
    note: '9 nodes · most popular',
    graph: graphOf(
      [
        ['competitors', 'starter.competitorWinners'],
        // Winners are already past 2×; this keeps a scheduled flow off repeats.
        ['cooldown', 'filter.cooldown', { days: 30 }],
        ['analyse', 'filter.analyse'],
        ['script', 'generate.script'],
        ['hooks', 'generate.hookVariations', { count: 5 }],
        ['persona', 'generate.personaVoice'],
        ['guardrails', 'guardrail.brandGuardrails'],
        ['approval', 'guardrail.humanApproval'],
        ['publish', 'output.schedulePublish'],
      ],
      chain(
        'competitors',
        'cooldown',
        'analyse',
        'script',
        'hooks',
        'persona',
        'guardrails',
        'approval',
        'publish',
      ),
    ),
  },
  {
    key: 'replicate-reel',
    name: 'Replicate a reel',
    description:
      'Remake a reel you saved from a competitor in your brand voice — 5 hooks each.',
    note: '8 nodes · from a saved reel',
    graph: graphOf(
      [
        ['reference', 'starter.referencePost'],
        ['analyse', 'filter.analyse'],
        ['script', 'generate.script'],
        ['hooks', 'generate.hookVariations', { count: 5 }],
        ['persona', 'generate.personaVoice'],
        ['guardrails', 'guardrail.brandGuardrails'],
        ['approval', 'guardrail.humanApproval'],
        ['publish', 'output.schedulePublish'],
      ],
      chain(
        'reference',
        'analyse',
        'script',
        'hooks',
        'persona',
        'guardrails',
        'approval',
        'publish',
      ),
    ),
  },
  {
    key: 'replicate-carousel',
    name: 'Remake a carousel',
    description:
      'Turn a carousel you saved from a competitor into your own, on your brand layout.',
    note: '7 nodes · no rendering',
    graph: graphOf(
      [
        ['reference', 'starter.referencePost'],
        ['analyse', 'filter.analyse'],
        ['script', 'generate.script'],
        ['slides', 'generate.carouselSlides', { slides: 7 }],
        ['guardrails', 'guardrail.brandGuardrails'],
        ['approval', 'guardrail.humanApproval'],
        ['publish', 'output.schedulePublish'],
      ],
      chain(
        'reference',
        'analyse',
        'script',
        'slides',
        'guardrails',
        'approval',
        'publish',
      ),
    ),
  },
  {
    key: 'hook-ab-test',
    name: 'Hook A/B test',
    description:
      'Keep the body, swap the opening. Publish 4 hooks and let watch time pick.',
    note: '9 nodes · 48 h to a winner',
    graph: graphOf(
      [
        ['top', 'starter.myTopPosts', { limit: 1, windowDays: 30 }],
        ['analyse', 'filter.analyse'],
        ['script', 'generate.script'],
        ['split', 'experiment.splitTest', { variable: 'hook', variants: 4 }],
        ['persona', 'generate.personaVoice'],
        ['guardrails', 'guardrail.brandGuardrails'],
        ['publish', 'output.schedulePublish'],
        ['delay', 'guardrail.delay', { hours: 48 }],
        ['pick', 'experiment.pickWinner', { metric: 'watchTime' }],
      ],
      [
        ['top', 'analyse'],
        ['analyse', 'script'],
        ['script', 'split'],
        ['split', 'persona'],
        ['persona', 'guardrails'],
        ['guardrails', 'publish'],
        ['publish', 'delay', 'published', 'in'],
        ['delay', 'pick'],
      ],
    ),
  },
  {
    key: 'carousel-from-top-post',
    name: 'Carousel from a top post',
    description:
      'Turn your best reel into a 7-slide carousel for Instagram and LinkedIn.',
    note: '6 nodes · no rendering',
    graph: graphOf(
      [
        ['top', 'starter.myTopPosts', { limit: 1 }],
        ['analyse', 'filter.analyse'],
        ['script', 'generate.script'],
        ['slides', 'generate.carouselSlides', { slides: 7 }],
        ['guardrails', 'guardrail.brandGuardrails'],
        ['publish', 'output.schedulePublish'],
      ],
      chain('top', 'analyse', 'script', 'slides', 'guardrails', 'publish'),
    ),
  },
  {
    key: 'slot-carousels',
    name: 'Carousels from your slide pools',
    description:
      'Every Monday, mix your slide images and on-slide captions into fresh carousels, caption them and schedule what you approve.',
    note: '6 nodes · weekly',
    graph: graphOf(
      [
        ['schedule', 'starter.schedule', { cron: '0 9 * * 1' }],
        ['slots', 'starter.slotCarousel', { count: 10 }],
        ['captions', 'generate.captionHashtags', { platforms: ['INSTAGRAM', 'TIKTOK'] }],
        ['guardrails', 'guardrail.brandGuardrails'],
        ['approval', 'guardrail.humanApproval'],
        ['publish', 'output.schedulePublish', { timing: 'spaced', spacingHours: 3 }],
      ],
      [
        ['schedule', 'slots', 'out', 'trigger'],
        ...chain('slots', 'captions', 'guardrails', 'approval', 'publish'),
      ],
    ),
  },
  {
    key: 'remake-for-personas',
    name: 'Remake for my personas',
    description:
      'Remake a hook image with every persona, build carousels with your captions and demo slides, and book each on its persona’s calendar.',
    note: '9 nodes · all personas',
    graph: graphOf(
      [
        // The hook to copy - one image, or switch to a collection.
        ['images', 'starter.collectionImages', { from: 'oneImage' }],
        ['vary', 'generate.personaVariations', { count: 10 }],
        // The plain shots, to reuse later (pick your Hook bank).
        ['bank', 'output.saveToLibrary', { tags: ['hook-base'] }],
        ['build', 'generate.buildCarousels', { count: 10 }],
        // The captioned hook slides (pick your Hook slides collection).
        [
          'slides',
          'output.saveToLibrary',
          { carouselParts: 'firstSlide', tags: ['hook-slide'] },
        ],
        ['captions', 'generate.captionHashtags', { platforms: ['INSTAGRAM', 'TIKTOK'] }],
        ['guardrails', 'guardrail.brandGuardrails'],
        ['approval', 'guardrail.humanApproval'],
        [
          'publish',
          'output.schedulePublish',
          { postTo: 'personaAccounts', timing: 'daily', dailyTimes: ['09:00', '18:00'] },
        ],
      ],
      [
        ['images', 'vary'],
        ['vary', 'bank'],
        ['vary', 'build'],
        ['build', 'slides'],
        ...chain('build', 'captions', 'guardrails', 'approval', 'publish'),
      ],
    ),
  },
  {
    key: 'replicate-scenes',
    name: 'Replicate scenes',
    description:
      'Remake a reel’s scenes as AI videos - a new person, no on-screen text - with Kling or Seedance, kept in your library.',
    note: '3 nodes · no persona',
    graph: graphOf(
      [
        // Pick the reel, and the scenes to remake (none ticked: all of them).
        ['scenes', 'starter.reelScenes'],
        [
          'video',
          'generate.sceneVideo',
          { videoModel: 'kling', duration: '5', format: '9:16', quality: 'high' },
        ],
        ['save', 'output.saveToLibrary', { tags: ['scene-replica'] }],
      ],
      chain('scenes', 'video', 'save'),
    ),
  },
  {
    key: 'animate-images',
    name: 'Animate my images',
    description:
      'One AI video per image in a collection - QR codes, products, stills - each animated as it is, with Kling or Seedance.',
    note: '3 nodes · one video per image',
    graph: graphOf(
      [
        // Pick the collection.
        ['images', 'starter.collectionImages', { from: 'collection' }],
        [
          'video',
          'generate.sceneVideo',
          {
            frame: 'asIs',
            videoModel: 'kling',
            duration: '5',
            format: '9:16',
            quality: 'high',
            prompt: 'Slow push in, subtle natural light changes.',
          },
        ],
        ['save', 'output.saveToLibrary', { tags: ['animated'] }],
      ],
      chain('images', 'video', 'save'),
    ),
  },
  {
    key: 'replicate-scenes-personas',
    name: 'Replicate scenes for my personas',
    description:
      'Remake a reel’s scenes with each persona’s face, animate every shot with Kling or Seedance, caption it and book it on its persona’s calendar after your review.',
    note: '8 nodes · all personas',
    graph: graphOf(
      [
        // Pick the reel, and the scenes to remake (none ticked: all of them).
        ['scenes', 'starter.reelScenes'],
        // Same setting and pose, the persona's face; vertical, like the reel.
        ['vary', 'generate.personaVariations', { count: 1, aspectRatio: '9:16' }],
        // A persona's shot is animated as it is - its face stays.
        [
          'video',
          'generate.sceneVideo',
          { videoModel: 'kling', duration: '5', format: '9:16', quality: 'high' },
        ],
        ['save', 'output.saveToLibrary', { tags: ['scene-replica'] }],
        ['captions', 'generate.captionHashtags', { platforms: ['INSTAGRAM', 'TIKTOK'] }],
        ['guardrails', 'guardrail.brandGuardrails'],
        ['approval', 'guardrail.humanApproval'],
        [
          'publish',
          'output.schedulePublish',
          { postTo: 'personaAccounts', timing: 'daily', dailyTimes: ['09:00', '18:00'] },
        ],
      ],
      [
        ...chain('scenes', 'vary', 'video'),
        ['video', 'save'],
        ...chain('video', 'captions', 'guardrails', 'approval', 'publish'),
      ],
    ),
  },
  {
    key: 'more-hooks-from-bank',
    name: 'More hooks from my bank',
    description:
      'New captions on the persona shots you already have: fresh carousels for each persona, no new images.',
    note: '7 nodes · no image cost',
    graph: graphOf(
      [
        // Your Hook bank: each shot already carries its persona.
        ['images', 'starter.collectionImages', { from: 'collection', limit: 100 }],
        ['build', 'generate.buildCarousels', { count: 10 }],
        [
          'slides',
          'output.saveToLibrary',
          { carouselParts: 'firstSlide', tags: ['hook-slide'] },
        ],
        ['captions', 'generate.captionHashtags', { platforms: ['INSTAGRAM', 'TIKTOK'] }],
        ['guardrails', 'guardrail.brandGuardrails'],
        ['approval', 'guardrail.humanApproval'],
        [
          'publish',
          'output.schedulePublish',
          { postTo: 'personaAccounts', timing: 'daily', dailyTimes: ['09:00', '18:00'] },
        ],
      ],
      [
        ['images', 'build'],
        ['build', 'slides'],
        ...chain('build', 'captions', 'guardrails', 'approval', 'publish'),
      ],
    ),
  },
  {
    key: 'localise-winners',
    name: 'Localise your winners',
    description: 'Translate winning reels and captions into Spanish and German.',
    note: '7 nodes · 2 languages',
    graph: graphOf(
      [
        ['top', 'starter.myTopPosts', { limit: 3 }],
        ['translate', 'edit.translate', { languages: ['es', 'de'] }],
        ['subtitles', 'edit.subtitles'],
        ['captions', 'generate.captionHashtags'],
        ['guardrails', 'guardrail.brandGuardrails'],
        ['approval', 'guardrail.humanApproval'],
        ['publish', 'output.schedulePublish'],
      ],
      chain(
        'top',
        'translate',
        'subtitles',
        'captions',
        'guardrails',
        'approval',
        'publish',
      ),
    ),
  },
];

export function getTemplate(key: string): FlowTemplate | undefined {
  return FLOW_TEMPLATES.find((template) => template.key === key);
}
